<mat-card class="manual-attendance-card">
  <mat-card-header>
    <mat-card-title>
      <mat-icon>edit_calendar</mat-icon>
      Monthly Manual Attendance Entry
    </mat-card-title>
    <mat-card-subtitle>
      Manage attendance records for employees not on the fingerprint system
    </mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    <!-- Selection Form -->
    <form [formGroup]="selectionForm" class="selection-form">
      <div class="form-row">
        <mat-form-field appearance="outline" class="employee-field">
          <mat-label>Select Employee</mat-label>
          <mat-select formControlName="employeeId" required>
            <mat-option *ngFor="let employee of employees" [value]="employee.id">
              {{ employee.firstName }} {{ employee.lastName }}
              <span *ngIf="employee.fingerprintId" class="fingerprint-info">
                (Fingerprint: {{ employee.fingerprintId }})
              </span>
              <span *ngIf="!employee.fingerprintId" class="no-fingerprint">
                (Will use MANUAL as Fingerprint ID)
              </span>
            </mat-option>
          </mat-select>
          <mat-error *ngIf="selectionForm.get('employeeId')?.hasError('required')">
            Please select an employee
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="year-field">
          <mat-label>Year</mat-label>
          <mat-select formControlName="year" required>
            <mat-option *ngFor="let year of [currentYear - 1, currentYear, currentYear + 1]" [value]="year">
              {{ year }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="month-field">
          <mat-label>Month</mat-label>
          <mat-select formControlName="month" required>
            <mat-option *ngFor="let month of [1,2,3,4,5,6,7,8,9,10,11,12]; let i = index" [value]="month">
              {{ monthNames[i] }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <button 
          mat-raised-button 
          color="primary" 
          (click)="loadMonthlyData()"
          [disabled]="!selectionForm.valid || isLoading"
          class="load-button">
          <mat-icon>refresh</mat-icon>
          Load Month
        </button>
      </div>
    </form>

    <!-- Monthly Grid -->
    <div *ngIf="monthlyData" class="monthly-grid-container">
      <div class="grid-header">
        <h3>
          {{ getSelectedEmployeeName() }} - {{ getMonthName(monthlyData.month) }} {{ monthlyData.year }}
          <span class="fingerprint-info">(Fingerprint: {{ monthlyData.fingerprintId }})</span>
        </h3>
        <div class="monthly-total">
          <strong>Total Monthly Hours: {{ monthlyData.totalMonthlyHours | number:'1.2-2' }}</strong>
        </div>
      </div>

      <div class="grid-table-container">
        <table class="attendance-grid-table">
          <thead>
            <tr>
              <th class="day-column">Day</th>
              <th class="date-column">Date</th>
              <th class="time-column">Login 1</th>
              <th class="time-column">Logout 1</th>
              <th class="time-column">Login 2</th>
              <th class="time-column">Logout 2</th>
              <th class="time-column">Login 3</th>
              <th class="time-column">Logout 3</th>
              <th class="breaks-column">Breaks (min)</th>
              <th class="total-column">Total Hours</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let day of monthlyData.dailyEntries; let i = index" 
                [class.existing-data]="day.hasExistingData">
              <td class="day-number">{{ day.dayNumber }}</td>
              <td class="date-cell">{{ day.date }}</td>
              
              <!-- Login/Logout Time Inputs -->
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.login1 || ''"
                  (change)="onTimeChange(i, 'login1', $event.target.value)"
                  class="time-field">
              </td>
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.logout1 || ''"
                  (change)="onTimeChange(i, 'logout1', $event.target.value)"
                  class="time-field">
              </td>
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.login2 || ''"
                  (change)="onTimeChange(i, 'login2', $event.target.value)"
                  class="time-field">
              </td>
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.logout2 || ''"
                  (change)="onTimeChange(i, 'logout2', $event.target.value)"
                  class="time-field">
              </td>
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.login3 || ''"
                  (change)="onTimeChange(i, 'login3', $event.target.value)"
                  class="time-field">
              </td>
              <td class="time-input">
                <input 
                  type="time" 
                  [value]="day.logout3 || ''"
                  (change)="onTimeChange(i, 'logout3', $event.target.value)"
                  class="time-field">
              </td>
              
              <!-- Breaks Input -->
              <td class="breaks-input">
                <input 
                  type="number" 
                  [value]="day.breaksMinutes || ''"
                  (change)="onBreaksChange(i, +$event.target.value)"
                  min="0"
                  max="480"
                  placeholder="0"
                  class="breaks-field">
              </td>
              
              <!-- Total Hours Display -->
              <td class="total-hours">
                <span [class.has-hours]="day.totalHours > 0">
                  {{ day.totalHours | number:'1.2-2' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Save Button -->
      <div class="save-section">
        <button 
          mat-raised-button 
          color="accent" 
          (click)="saveAllChanges()"
          [disabled]="isSaving"
          class="save-button">
          <mat-icon>save</mat-icon>
          {{ isSaving ? 'Saving...' : 'Save All Changes' }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-container">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading monthly data...</p>
    </div>

    <!-- Instructions -->
    <mat-card *ngIf="!monthlyData && !isLoading" class="instructions-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>info</mat-icon>
          Instructions
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <ol>
          <li>Select an employee from the dropdown</li>
          <li>Choose the year and month for attendance entry</li>
          <li>Click "Load Month" to view the monthly grid</li>
          <li>Enter login/logout times for each day (up to 3 sessions per day)</li>
          <li>Add break time in minutes if applicable</li>
          <li>Total hours are calculated automatically</li>
          <li>Click "Save All Changes" to save all entries at once</li>
        </ol>
        <p class="note">
          <strong>Note:</strong> The system will use the employee's actual fingerprint ID 
          if available, or "MANUAL" as fallback. Existing data will be pre-populated and 
          can be edited. All changes are saved to the attendance table.
        </p>
      </mat-card-content>
    </mat-card>
  </mat-card-content>
</mat-card>